import React, { useState } from "react";
import { ChevronDown, ChevronUp, Heart, AlertTriangle, Sparkles, Clock, Mic, User } from "lucide-react";

// Sprint 10 — Flexpfad-Bibliothek-Anzeige (4-Sektionen-Architektur).
//   1. State-Shift (Anerkennung)
//   2. Hauptbaustein (Text + Outcome + Warnung)
//   3. 3 Stellschrauben (einklappbar)
//   4. Mikro-Challenge (heute machbar)

const FOKUSTYP_LABEL = { katze: "Katze", eule: "Eule", biene: "Biene", hund: "Hund" };
const FOKUSTYP_COLOR = {
  katze: "#FBBF24",
  eule: "#3B82F6",
  biene: "#10B981",
  hund: "#F97316",
};

export default function HeartPiece({ heartPiece }) {
  if (!heartPiece) return null;
  const { state_shift, hauptbaustein, stellschrauben, challenge } = heartPiece;
  const accent = FOKUSTYP_COLOR[hauptbaustein?.fokustyp] || "#4563BB";

  return (
    <section
      data-testid="heart-piece"
      className="space-y-5"
      aria-label="Flexpfad-Bibliothek: Dein konkreter Anker für dieses Gespräch"
    >
      {/* 1. State-Shift */}
      <div
        data-testid="hp-state-shift"
        className="flex items-start gap-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 px-4 py-3"
      >
        <Sparkles className="w-5 h-5 text-emerald-700 dark:text-emerald-300 flex-shrink-0 mt-0.5" />
        <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100 leading-relaxed">
          {state_shift}
        </p>
      </div>

      {/* 2. Hauptbaustein */}
      {hauptbaustein && (
        <div
          data-testid="hp-hauptbaustein"
          className="rounded-2xl border-2 bg-white dark:bg-slate-900 p-5 sm:p-6 shadow-sm"
          style={{ borderColor: accent }}
        >
          <div className="flex items-center gap-2 mb-3">
            <Heart className="w-4 h-4" style={{ color: accent }} />
            <p className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>
              Hauptbaustein · {FOKUSTYP_LABEL[hauptbaustein.fokustyp] || "—"}
            </p>
          </div>
          <h3
            data-testid="hp-hauptbaustein-text"
            className="text-xl sm:text-2xl font-extrabold tracking-tight text-stone-900 dark:text-white"
          >
            {hauptbaustein.text}
          </h3>
          <div
            data-testid="hp-outcome"
            className="mt-4 rounded-lg bg-stone-50 dark:bg-slate-800/70 border-l-4 px-4 py-3"
            style={{ borderLeftColor: accent }}
          >
            <p className="text-xs font-bold uppercase tracking-wider text-stone-500 dark:text-slate-400 mb-1">
              Warum das wirkt
            </p>
            <p className="text-sm text-stone-800 dark:text-slate-200 leading-relaxed">{hauptbaustein.outcome}</p>
          </div>
          <div
            data-testid="hp-warnung"
            className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-100 dark:border-red-900/60 px-4 py-3"
          >
            <AlertTriangle className="w-4 h-4 text-red-700 dark:text-red-300 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-red-700 dark:text-red-300 mb-0.5">
                Was die Tür schließt
              </p>
              <p className="text-sm text-red-900 dark:text-red-100 leading-relaxed">{hauptbaustein.warnung}</p>
            </div>
          </div>
        </div>
      )}

      {/* 3. Stellschrauben (einklappbar) */}
      {stellschrauben && (
        <div data-testid="hp-stellschrauben" className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-wider text-stone-500 dark:text-slate-400 px-1">
            3 Stellschrauben
          </p>
          <Stellschraube
            testid="hp-stellschraube-wann"
            icon={Clock}
            label="Wann"
            text={stellschrauben.wann}
            accent={accent}
          />
          <Stellschraube
            testid="hp-stellschraube-ton"
            icon={Mic}
            label="Wie (Ton)"
            text={stellschrauben.wie_ton}
            accent={accent}
          />
          <Stellschraube
            testid="hp-stellschraube-koerper"
            icon={User}
            label="Wie (Körper)"
            text={stellschrauben.wie_koerper}
            accent={accent}
          />
        </div>
      )}

      {/* 4. Mikro-Challenge */}
      {challenge && (
        <div
          data-testid="hp-challenge"
          className="rounded-xl bg-violet-50 dark:bg-violet-950/40 border border-violet-200 dark:border-violet-800/60 px-4 py-4"
        >
          <p className="text-xs font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300 mb-1.5">
            Heute · Mikro-Challenge
          </p>
          <p className="text-sm text-violet-950 dark:text-violet-100 leading-relaxed">{challenge}</p>
        </div>
      )}
    </section>
  );
}

function Stellschraube({ testid, icon: Icon, label, text, accent }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={testid}
      className="rounded-lg border border-stone-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testid}-toggle`}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-stone-50 dark:hover:bg-slate-800 transition-colors"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-stone-800 dark:text-slate-100">
          <Icon className="w-4 h-4" style={{ color: accent }} />
          {label}
        </span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-stone-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-stone-400" />
        )}
      </button>
      {open && (
        <div className="px-4 pb-3 -mt-1">
          <p className="text-sm text-stone-700 dark:text-slate-300 leading-relaxed">{text}</p>
        </div>
      )}
    </div>
  );
}
